import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useOverflowScrollbar } from "../hooks/useOverflowScrollbar";
import { getConstrainedSelectPanelStyle, getViewportBox } from "./constrainedSelectPanelStyle";
import "./ConstrainedSelect.css";

/**
 * @typedef {{ value: string, label: string, disabled?: boolean }} ConstrainedSelectOption
 */

/**
 * Custom listbox whose panel is portaled to the viewport.
 * Native <select> popups on desktop ignore CSS and grow with the longest title.
 * @param {{
 *   id?: string,
 *   className?: string,
 *   value: string,
 *   options: ConstrainedSelectOption[],
 *   onChange: (value: string) => void,
 *   disabled?: boolean,
 *   ariaLabel?: string,
 *   style?: import("react").CSSProperties,
 *   title?: string,
 *   required?: boolean,
 *   ariaBusy?: boolean,
 * }} props
 */
export default function ConstrainedSelect({
  id,
  className = "",
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel,
  style,
  title,
  required = false,
  ariaBusy = false,
}) {
  const generatedId = useId();
  const triggerId = id || generatedId;
  const listboxId = `${triggerId}-listbox`;
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    scrollRef,
    trackRef,
    metrics,
    handleThumbPointerDown,
    handleTrackPointerDown,
  } = useOverflowScrollbar(open);

  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );
  const enabledIndexes = useMemo(
    () => options.map((option, index) => (option.disabled ? -1 : index)).filter((index) => index >= 0),
    [options],
  );

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const selectIndex = useCallback((index) => {
    const option = options[index];
    if (!option || option.disabled) {
      return;
    }
    onChange(option.value);
    setOpen(false);
  }, [onChange, options]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const selectedIndex = options.findIndex((option) => option.value === value);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : (enabledIndexes[0] ?? 0));

    const onPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [enabledIndexes, open, options, value]);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    function updatePosition() {
      const panel = panelRef.current;
      const root = rootRef.current;
      if (!panel || !root) {
        return;
      }
      const visualViewport = window.visualViewport;
      const viewport = getViewportBox({
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        clientWidth: document.documentElement?.clientWidth || window.innerWidth,
        clientHeight: document.documentElement?.clientHeight || window.innerHeight,
        visualWidth: visualViewport?.width,
        visualHeight: visualViewport?.height,
      });
      const panelStyle = getConstrainedSelectPanelStyle(
        root.getBoundingClientRect(),
        viewport,
      );
      panel.style.top = `${panelStyle.top}px`;
      panel.style.left = `${panelStyle.left}px`;
      panel.style.width = `${panelStyle.width}px`;
      panel.style.maxWidth = `${panelStyle.width}px`;
      panel.style.maxHeight = `${panelStyle.maxHeight}px`;
      panel.style.setProperty("--constrained-select-width", `${panelStyle.width}px`);
      panel.style.setProperty("--constrained-select-max-height", `${panelStyle.maxHeight}px`);
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.visualViewport?.addEventListener("resize", updatePosition);
    window.visualViewport?.addEventListener("scroll", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.visualViewport?.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("scroll", updatePosition);
    };
  }, [open, options.length]);

  const moveActive = (direction) => {
    if (enabledIndexes.length === 0) {
      return;
    }
    const currentPos = enabledIndexes.indexOf(activeIndex);
    const start = currentPos >= 0 ? currentPos : 0;
    const nextPos = (start + direction + enabledIndexes.length) % enabledIndexes.length;
    setActiveIndex(enabledIndexes[nextPos]);
  };

  const onTriggerKeyDown = (event) => {
    if (disabled) {
      return;
    }
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const onListKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectIndex(activeIndex);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };

  const rootClassName = ["constrained-select", className].filter(Boolean).join(" ");

  return (
    <div className={rootClassName} ref={rootRef} data-testid="constrained-select" style={style}>
      <button
        type="button"
        id={triggerId}
        className="constrained-select__trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        aria-required={required || undefined}
        aria-busy={ariaBusy || undefined}
        title={title}
        data-value={value}
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setOpen((prev) => !prev);
          }
        }}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="constrained-select__value">
          {selected?.label || ""}
        </span>
        <span className="constrained-select__chevron" aria-hidden="true">▾</span>
      </button>
      {open && typeof document !== "undefined"
        ? createPortal(
          <div
            ref={panelRef}
            className="constrained-select__panel"
            data-testid="constrained-select-panel"
          >
            <div className="constrained-select__scroll">
              <ul
                ref={scrollRef}
                id={listboxId}
                className="constrained-select__list"
                role="listbox"
                tabIndex={-1}
                aria-labelledby={triggerId}
                aria-activedescendant={options[activeIndex] ? `${listboxId}-opt-${activeIndex}` : undefined}
                onKeyDown={onListKeyDown}
              >
                {options.map((option, index) => (
                  <li key={`${option.value}-${index}`}>
                    <button
                      type="button"
                      id={`${listboxId}-opt-${index}`}
                      role="option"
                      className={`constrained-select__option${option.value === value ? " is-selected" : ""}${index === activeIndex ? " is-active" : ""}`}
                      aria-selected={option.value === value}
                      disabled={option.disabled === true}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => selectIndex(index)}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
              <div
                ref={trackRef}
                className={`constrained-select__scrollbar${metrics.canScroll ? " is-visible" : ""}`}
                data-testid="constrained-select-scrollbar"
                aria-hidden="true"
                onPointerDown={handleTrackPointerDown}
              >
                <div
                  className="constrained-select__thumb"
                  style={{
                    height: `${metrics.thumbHeight}px`,
                    transform: `translateY(${metrics.thumbOffset}px)`,
                  }}
                  onPointerDown={handleThumbPointerDown}
                />
              </div>
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

ConstrainedSelect.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
  }).isRequired).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  title: PropTypes.string,
  required: PropTypes.bool,
  ariaBusy: PropTypes.bool,
};
